import html2canvas from 'html2canvas';
import { Services } from '../../services';

export const MAP_WRAPPER_SELECTOR = '.c-MapWrapper';

// Render the capture at 2x for crisp output in the PDF.
const CAPTURE_SCALE = 2;

// Web Mercator ground resolution at zoom 0, latitude 0: metres per CSS pixel for a 256px tile world.
const EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392;

// The print frame fills this fraction of the smaller viewport dimension, leaving a dimmed margin so
// the user keeps surrounding context while framing.
const FRAME_FILL = 0.9;

export interface MapCapture {
	canvas: HTMLCanvasElement;
	/**
	 * Ground distance (metres) represented by one pixel of {@link canvas}. Combined with the image's
	 * on-page scale in the PDF, this yields a true-to-ground scale bar regardless of zoom or screen.
	 */
	metersPerImagePixel: number;
	/**
	 * Google's live attribution/copyright text (e.g. "Imagery ©2026 Airbus, Maxar Technologies"). The
	 * print frame crops out Google's on-map credit, so we capture the text and re-draw it in the PDF
	 * to stay compliant with the Maps Terms of Service.
	 */
	attribution: string;
}

/** A crop rectangle in CSS pixels, relative to the map wrapper's top-left. */
export interface CaptureRegion {
	x: number;
	y: number;
	width: number;
	height: number;
}

/**
 * The largest rectangle of the given aspect ratio that fits (centered) within the on-screen map,
 * in CSS pixels relative to the wrapper. This is both the visible print frame and the capture crop,
 * so what the user frames is exactly what the PDF receives. Returns `null` if the map isn't laid out.
 */
export function printFrameRegion(aspect: number): CaptureRegion | null {
	const mapElement = document.querySelector<HTMLElement>(MAP_WRAPPER_SELECTOR);
	if (!mapElement) {
		return null;
	}
	const { width, height } = mapElement.getBoundingClientRect();
	if (width <= 0 || height <= 0) {
		return null;
	}

	let frameWidth = width * FRAME_FILL;
	let frameHeight = frameWidth / aspect;
	if (frameHeight > height * FRAME_FILL) {
		frameHeight = height * FRAME_FILL;
		frameWidth = frameHeight * aspect;
	}

	return {
		x: (width - frameWidth) / 2,
		y: (height - frameHeight) / 2,
		width: frameWidth,
		height: frameHeight,
	};
}

const LINE_SVG_SELECTOR = '.c-TopologicalEditorMapLine__vector, .c-TopologicalEditorMapPolyline__vector';
const SVG_BOX_PADDING = 6;
// Slack around a centered <text> anchor to cover glyph width/ascent so labels aren't clipped.
const TEXT_BOX_MARGIN = 12;

// Google UI controls to drop from the export
const IGNORED_CAPTURE_SELECTOR =
	'.gm-style-mtc-bbw, .gm-bundled-control, .gm-svpc, .gm-fullscreen-control, .c-PhotomeasureOverlay';

/**
 * Rasterize the current map viewport (basemap + IKE overlays) to a canvas. Google's UI controls are
 * dropped via `ignoreElements`. The print-frame crop sits inside the viewport, so Google's on-map
 * logo/copyright fall outside it — we read the copyright text here and re-print it in the PDF footer
 * instead (see {@link MapCapture.attribution}), as required by the Maps Terms of Service.
 */
export async function captureMap(region?: CaptureRegion): Promise<MapCapture> {
	const mapElement = document.querySelector<HTMLElement>(MAP_WRAPPER_SELECTOR);
	if (!mapElement) {
		throw new Error('Map element not found for capture');
	}

	const attribution = googleAttribution(mapElement);

	const canvas = await html2canvas(mapElement, {
		useCORS: true,
		logging: false,
		backgroundColor: null,
		scale: CAPTURE_SCALE,
		ignoreElements: (element) => element.matches(IGNORED_CAPTURE_SELECTOR),
		onclone: (clonedDocument) => giveLineSvgsABox(clonedDocument),
		// Crop to the print frame so the capture's aspect ratio matches the PDF map slot exactly.
		...(region && { x: region.x, y: region.y, width: region.width, height: region.height }),
	});

	return { canvas, metersPerImagePixel: currentMetersPerCssPixel() / CAPTURE_SCALE, attribution };
}

/**
 * Read Google's current attribution/copyright text from the map. We use the `.gmnoscreen` elements —
 * Google's print/screen-reader credit copies — which hold the clean credit string (e.g. "Imagery
 * ©2026 Airbus") without the hidden control labels that pollute the on-screen `.gm-style-cc` markup.
 * Returns an empty string if none is found.
 */
function googleAttribution(mapElement: HTMLElement): string {
	const credits = new Set<string>();
	mapElement.querySelectorAll<HTMLElement>('.gmnoscreen').forEach((element) => {
		const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
		if (text.includes('©')) {
			credits.add(text);
		}
	});
	return Array.from(credits).join(', ');
}

/**
 * Ground resolution (metres per CSS pixel) at the map's current zoom and centre latitude, using the
 * standard Web Mercator formula. Returns 0 when the map isn't ready, which disables the scale bar.
 */
function currentMetersPerCssPixel(): number {
	const map = Services.Map.map;
	const zoom = map.getZoom();
	const center = map.getCenter();
	if (zoom == null || center == null) {
		return 0;
	}
	const latitudeRadians = (center.lat() * Math.PI) / 180;
	return (EQUATOR_METERS_PER_PIXEL_AT_ZOOM_0 * Math.cos(latitudeRadians)) / 2 ** zoom;
}

/**
 * Span lines are drawn in SVGs with `overflow: visible` and no intrinsic size — anchored at the
 * Google overlay pane origin (off-screen), they paint via overflow but html2canvas clips them to
 * their empty box. In the throwaway capture clone, give each line SVG a tight box positioned at
 * its line's bounding-box origin and rebase the child coordinates to be box-local, so html2canvas
 * rasterizes the real SVG correctly.
 */
function giveLineSvgsABox(clonedDocument: Document): void {
	clonedDocument.querySelectorAll<SVGSVGElement>(LINE_SVG_SELECTOR).forEach((svg) => {
		const shapes = Array.from(svg.querySelectorAll<SVGGraphicsElement>('line, circle, text, polyline'));
		const box = boundingBox(shapes);
		if (!box) {
			return;
		}

		const originX = box.minX - SVG_BOX_PADDING;
		const originY = box.minY - SVG_BOX_PADDING;
		const width = box.maxX - box.minX + SVG_BOX_PADDING * 2;
		const height = box.maxY - box.minY + SVG_BOX_PADDING * 2;

		// Position via a wrapper div, not the svg itself: html2canvas serializes the svg (with its
		// inline styles) into a standalone image, and Firefox applies position+left/top INSIDE that
		// image document, shifting the drawing out of its own viewport. The inline styles themselves
		// come from html2canvas copying the original's full computed style onto the clone — drop them.
		const wrapper = clonedDocument.createElement('div');
		wrapper.style.position = 'absolute';
		wrapper.style.left = `${originX}px`;
		wrapper.style.top = `${originY}px`;
		wrapper.style.width = `${width}px`;
		wrapper.style.height = `${height}px`;
		svg.parentNode?.insertBefore(wrapper, svg);
		wrapper.appendChild(svg);
		svg.removeAttribute('style');
		svg.style.overflow = 'visible';
		svg.setAttribute('width', `${width}`);
		svg.setAttribute('height', `${height}`);

		for (const shape of shapes) {
			rebaseShape(shape, originX, originY);
		}
	});
}

interface Box {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

function boundingBox(shapes: Element[]): Box | null {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const shape of shapes) {
		if (shape.tagName === 'circle') {
			const cx = num(shape, 'cx');
			const cy = num(shape, 'cy');
			const r = num(shape, 'r');
			minX = Math.min(minX, cx - r);
			maxX = Math.max(maxX, cx + r);
			minY = Math.min(minY, cy - r);
			maxY = Math.max(maxY, cy + r);
		} else if (shape.tagName === 'text') {
			// text-anchor=middle glyphs extend horizontally around x and up from the baseline y;
			// pad so centered labels/markers aren't clipped when a midpoint sits at a box extreme.
			const x = num(shape, 'x');
			const y = num(shape, 'y');
			minX = Math.min(minX, x - TEXT_BOX_MARGIN);
			maxX = Math.max(maxX, x + TEXT_BOX_MARGIN);
			minY = Math.min(minY, y - TEXT_BOX_MARGIN);
			maxY = Math.max(maxY, y + TEXT_BOX_MARGIN);
		} else if (shape.tagName === 'polyline') {
			for (const [x, y] of polylinePoints(shape)) {
				minX = Math.min(minX, x);
				maxX = Math.max(maxX, x);
				minY = Math.min(minY, y);
				maxY = Math.max(maxY, y);
			}
		} else {
			const x1 = num(shape, 'x1');
			const x2 = num(shape, 'x2');
			const y1 = num(shape, 'y1');
			const y2 = num(shape, 'y2');
			minX = Math.min(minX, x1, x2);
			maxX = Math.max(maxX, x1, x2);
			minY = Math.min(minY, y1, y2);
			maxY = Math.max(maxY, y1, y2);
		}
	}

	return Number.isFinite(minX) && Number.isFinite(maxX) ? { minX, minY, maxX, maxY } : null;
}

function rebaseShape(shape: SVGGraphicsElement, originX: number, originY: number): void {
	if (shape.tagName === 'circle') {
		shape.setAttribute('cx', `${num(shape, 'cx') - originX}`);
		shape.setAttribute('cy', `${num(shape, 'cy') - originY}`);
		// cx/cy are CSS geometry properties too — drop the inlined absolute values (see text below).
		shape.style.removeProperty('cx');
		shape.style.removeProperty('cy');
		return;
	}
	if (shape.tagName === 'text') {
		shape.setAttribute('x', `${num(shape, 'x') - originX}`);
		shape.setAttribute('y', `${num(shape, 'y') - originY}`);
		// Rotated labels pivot about absolute coordinates baked into the transform; rebase the
		// pivot too or the rotation translates the glyph away from the line.
		const rotate = shape.getAttribute('transform')?.match(/rotate\(([-\d.]+)[ ,]+([-\d.]+)[ ,]+([-\d.]+)\)/);
		if (rotate) {
			shape.setAttribute(
				'transform',
				`rotate(${rotate[1]} ${Number(rotate[2]) - originX} ${Number(rotate[3]) - originY})`
			);
		}
		// html2canvas copies the original's computed styles inline onto the clone; the inline CSS
		// transform is a matrix baked with the absolute pivot and shadows the rebased attribute.
		shape.style.removeProperty('transform');
		return;
	}
	if (shape.tagName === 'polyline') {
		const rebased = polylinePoints(shape)
			.map(([x, y]) => `${x - originX},${y - originY}`)
			.join(' ');
		shape.setAttribute('points', rebased);
		return;
	}
	shape.setAttribute('x1', `${num(shape, 'x1') - originX}`);
	shape.setAttribute('x2', `${num(shape, 'x2') - originX}`);
	shape.setAttribute('y1', `${num(shape, 'y1') - originY}`);
	shape.setAttribute('y2', `${num(shape, 'y2') - originY}`);
}

function num(element: Element, attribute: string): number {
	return Number(element.getAttribute(attribute)) || 0;
}

function polylinePoints(shape: Element): Array<[number, number]> {
	const raw = shape.getAttribute('points') ?? '';
	return raw
		.trim()
		.split(/\s+/)
		.map((pair) => pair.split(','))
		.filter((parts) => parts.length === 2)
		.map(([x, y]) => [Number(x) || 0, Number(y) || 0]);
}

/**
 * Resolve after two animation frames so pending redraws (modal teardown, control hiding, label
 * toggling) have been painted before the map is captured.
 */
export function waitForRender(): Promise<void> {
	return new Promise((resolve) => {
		window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
	});
}
